import { Check, X, ArrowUpCircle, ArrowDownCircle, Send, LineChart, Lock, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { ScoreRing } from "./ScoreRing";
import { apiPost, fmt } from "@/lib/api";
import { alertChip } from "@/lib/terminalUtils";
import { market } from "@/lib/symbols";

export const SetupCard = ({ sig, balance, riskPct, onViewChart }) => {
  if (!sig) return null;
  if (sig.admin_off) {
    return (
      <div className="border border-white/10 bg-[#0d1219] p-6 text-center" data-testid="setup-admin-off">
        <Lock size={22} className="mx-auto text-mute-dim" />
        <p className="mt-3 font-display text-lg font-bold text-white">Pair Dinonaktifkan Admin</p>
        <p className="mx-auto mt-2 max-w-xs font-mono text-[11px] leading-relaxed text-mute">
          Sinyal pair ini sedang dimatikan admin (mode backtest). Harga, chart, dan berita tetap berjalan normal.
        </p>
      </div>
    );
  }
  if (sig.locked) {
    return (
      <div className="border border-warn/30 bg-[#0d1219] p-6 text-center" data-testid="setup-locked">
        <Lock size={22} className="mx-auto text-warn" />
        <p className="mt-3 font-display text-lg font-bold text-white">Akses Sinyal Terkunci</p>
        <p className="mx-auto mt-2 max-w-xs font-mono text-[11px] leading-relaxed text-mute">
          Verifikasi email Anda lewat ikon profil di kanan atas untuk membuka trial 3 hari.
          Jika masa akses habis atau dikunci admin, hubungi admin untuk membukanya.
        </p>
        <button onClick={() => window.dispatchEvent(new Event("open-support"))} data-testid="setup-contact-admin"
          className="mx-auto mt-4 flex h-9 items-center gap-2 border border-buy bg-buy/10 px-4 font-mono text-[11px] font-bold uppercase tracking-wider text-buy transition-colors hover:bg-buy hover:text-ink">
          <MessageCircle size={13} /> Hubungi Admin
        </button>
      </div>
    );
  }
  const d = market[sig.symbol]?.d ?? 2;
  const dir = sig.direction;
  const checklist = sig.checklist || {};
  const riskAmt = balance != null ? ((balance * (riskPct ?? 1)) / 100).toFixed(2) : null;

  const notifyTelegram = async () => {
    try {
      await apiPost("/notify/telegram", {});
    } catch (e) {
      toast.error("Telegram belum terhubung", {
        description: e.response?.data?.detail || "Kirim bot token dari @BotFather untuk mengaktifkan.",
      });
    }
  };

  return (
    <div className="border border-white/10 bg-[#0d1219] p-4" data-testid="setup-card">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-mute-dim">Current Setup</p>
        <span className={`border px-2 py-0.5 font-mono text-[9px] font-bold ${alertChip(sig.alert)}`} data-testid="setup-alert">
          {sig.alert === "VALID SETUP" ? "HIGH QUALITY SETUP" : sig.alert}
        </span>
      </div>

      <div className="mt-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {dir === "BUY"
            ? <ArrowUpCircle size={34} className="text-buy" />
            : dir === "SELL"
            ? <ArrowDownCircle size={34} className="text-sell" />
            : <ArrowUpCircle size={34} className="text-mute-dim" />}
          <div>
            <span className={`font-display text-2xl font-black ${dir === "BUY" ? "text-buy" : dir === "SELL" ? "text-sell" : "text-mute"}`} data-testid="setup-direction">
              {dir || "NO SETUP"}
            </span>
            <p className="font-mono text-[10px] text-mute-dim">{sig.label}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] text-mute">Score</span>
          <ScoreRing score={sig.total} />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 font-mono text-xs">
        {[["Entry", sig.entry, "text-white"], ["Stop Loss", sig.sl, "text-sell"],
          ["Take Profit 1", sig.tp1, "text-buy"], ["Take Profit 2", sig.tp2, "text-buy"],
          ["Risk / Reward", sig.rr, "text-warn"], ["Posisi ideal", riskAmt ? `$${riskAmt} risk` : "—", "text-white"]]
          .map(([k, v, cls]) => (
            <div key={k} className="flex justify-between">
              <span className="text-mute">{k}</span>
              <span className={`font-bold ${cls}`}>{typeof v === "number" ? fmt(v, d) : v ?? "—"}</span>
            </div>
          ))}
      </div>

      <div className="mt-4 space-y-1.5 border-t border-white/5 pt-3 font-mono text-[11px]">
        {Object.entries(checklist).map(([k, ok]) => (
          <div key={k} className="flex items-center justify-between" data-testid={`check-${k.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <span className="text-mute">{k}</span>
            {ok ? <Check size={13} className="text-buy" /> : <X size={13} className="text-mute-dim" />}
          </div>
        ))}
      </div>

      <div className="mt-4 flex gap-2">
        <button onClick={onViewChart} data-testid="view-chart-button"
          className="flex h-10 flex-1 items-center justify-center gap-2 bg-buy font-mono text-xs font-bold uppercase tracking-wider text-ink transition-colors hover:bg-buy/85">
          <LineChart size={14} /> View Chart
        </button>
        <button onClick={notifyTelegram} data-testid="notify-telegram-button"
          className="flex h-10 flex-1 items-center justify-center gap-2 border border-brand/50 bg-brand/10 font-mono text-xs font-bold uppercase tracking-wider text-[#8b97f5] transition-colors hover:bg-brand/25">
          <Send size={14} /> Notify Telegram
        </button>
      </div>

      {sig.reasons?.length > 0 && (
        <div className="mt-3 border-l-2 border-sell/60 pl-3">
          {sig.reasons.slice(0, 3).map((r) => (
            <p key={r} className="font-mono text-[10px] leading-relaxed text-mute">· {r}</p>
          ))}
        </div>
      )}
    </div>
  );
};
